import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { LogoFull } from "@/components/Logo";
import { ARTICLES } from "@/data/articles";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Clock, User, CheckCircle, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.livebetter.one";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

function BlogNewsletter() {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubscribe = async () => {
    if (!email.trim() || subscribing) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setResult({ type: "error", message: "Please enter a valid email." });
      return;
    }
    setSubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), source: "blog" }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult({ type: "success", message: data.message || "You're in!" });
        setEmail("");
      } else {
        setResult({ type: "error", message: data.detail || "Something went wrong." });
      }
    } catch {
      setResult({ type: "error", message: "Network error. Please try again." });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section className="px-6 md:px-12 lg:px-24 py-12 max-w-5xl mx-auto">
      <motion.div {...fadeUp()} className="lb-card text-center !py-10">
        <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "Outfit", color: "var(--lb-text)" }}>Get weekly health insights</h3>
        <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: "var(--lb-text-muted)" }}>
          Join 5,000+ readers. Science-backed, no spam, unsubscribe anytime.
        </p>
        {result?.type === "success" ? (
          <div className="flex items-center justify-center gap-2" style={{ color: "#2EAF7D" }}>
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-semibold">{result.message}</span>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                data-testid="blog-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setResult(null); }}
                onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                className="flex-1 rounded-full px-5 py-3 text-sm border outline-none focus:ring-2"
                style={{ background: "var(--lb-bg)", borderColor: "var(--lb-border)", color: "var(--lb-text)" }}
              />
              <Button className="lb-gradient-btn !px-6" onClick={handleSubscribe} disabled={subscribing}>
                {subscribing ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Subscribing...</>
                ) : (
                  <>Subscribe <ArrowRight className="ml-1 w-3.5 h-3.5" /></>
                )}
              </Button>
            </div>
            {result?.type === "error" && (
              <p className="text-xs mt-2" style={{ color: "#D45B5B" }}>{result.message}</p>
            )}
          </>
        )}
      </motion.div>
    </section>
  );
}

export default function BlogPage() {
  const navigate = useNavigate();
  const featured = ARTICLES[0];
  const rest = ARTICLES.slice(1);

  return (
    <div className="min-h-screen" style={{ background: "var(--lb-bg)" }}>
      <nav className="flex items-center justify-between px-6 md:px-12 py-5 max-w-7xl mx-auto">
        <LogoFull iconSize={34} className="cursor-pointer" onClick={() => navigate("/")} />
        <Button onClick={() => navigate("/")} className="lb-gradient-btn !px-6 !py-2 text-sm">Get Started</Button>
      </nav>

      <section className="px-6 md:px-12 lg:px-24 pt-10 pb-8 max-w-5xl mx-auto">
        <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm mb-6" style={{ color: "var(--lb-text-muted)" }}>
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>
        <motion.h1 {...fadeUp()} className="lb-heading-1 mb-3">Health Intelligence Blog</motion.h1>
        <motion.p {...fadeUp(0.08)} className="lb-body text-lg max-w-xl">
          Science-backed insights on sleep, stress, recovery, and emotional wellbeing. No fluff, just actionable knowledge.
        </motion.p>
      </section>

      {/* Featured */}
      <section className="px-6 md:px-12 lg:px-24 py-6 max-w-5xl mx-auto">
        <motion.div {...fadeUp()} onClick={() => navigate(`/blog/${featured.slug}`)}
          className="rounded-2xl overflow-hidden relative group cursor-pointer transition-all hover:shadow-xl"
          style={{ background: `linear-gradient(135deg, #025067 0%, #0B9FBD 50%, #B31B6F 100%)` }}>
          <div className="p-8 md:p-12 relative z-10">
            <span className="text-xs font-semibold tracking-wider uppercase px-3 py-1 rounded-full text-white/80"
              style={{ background: "rgba(255,255,255,0.15)" }}>Featured</span>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mt-4 mb-3 group-hover:underline underline-offset-4 decoration-white/30" style={{ fontFamily: "Outfit" }}>
              {featured.title}
            </h2>
            <p className="text-white/65 max-w-lg text-sm leading-relaxed">{featured.excerpt}</p>
            <div className="flex items-center gap-4 mt-5">
              <span className="text-xs text-white/50 flex items-center gap-1"><Clock className="w-3 h-3" /> {featured.readTime} read</span>
              <span className="text-xs text-white/50 flex items-center gap-1"><User className="w-3 h-3" /> {featured.author}</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Grid */}
      <section className="px-6 md:px-12 lg:px-24 py-8 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {rest.map((article, i) => (
            <motion.article key={i} {...fadeUp(i * 0.06)}
              onClick={() => navigate(`/blog/${article.slug}`)}
              className="lb-card-interactive !p-0 overflow-hidden group cursor-pointer">
              <div className="h-1.5 w-full" style={{ background: article.color }} />
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full"
                    style={{ background: `${article.color}12`, color: article.color }}>{article.category}</span>
                  <span className="text-[10px] flex items-center gap-1" style={{ color: "var(--lb-text-muted)" }}>
                    <Clock className="w-2.5 h-2.5" /> {article.readTime}
                  </span>
                </div>
                <h3 className="text-sm font-semibold mb-2 leading-snug group-hover:underline underline-offset-2"
                  style={{ fontFamily: "Outfit", color: "var(--lb-text)" }}>{article.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: "var(--lb-text-muted)" }}>
                  {article.excerpt.slice(0, 100)}...
                </p>
                <p className="text-[10px] mt-3 flex items-center gap-1" style={{ color: "var(--lb-text-muted)" }}>
                  <User className="w-2.5 h-2.5" /> {article.author}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <BlogNewsletter />

      <footer className="px-6 md:px-12 py-8 max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <LogoFull iconSize={24} />
        <p className="text-xs" style={{ color: "var(--lb-text-muted)" }}>LiveBetter is a wellness companion, not a medical device.</p>
      </footer>
    </div>
  );
}
